/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — quiet provenance on the sheet, and the guard
   that keeps a client variant honest (Masters Ladder R3, §2.4–2.5).

   Two jobs, one plugin, because both need the same per-node walk:

     1. PROVENANCE (decorations, never document content — the same rule
        the numbering plugin lives by). Core is unmarked. A layer block
        gets a thin edge and a "‹Client›" tag; an option's block gets
        "⇄"; an overridden block gets a quiet marker with one-click
        revert; a stale one says "Core has changed"; a hidden one is a
        dashed stub with "restore". Tokens do the colouring — this is
        deliberately NOT a Christmas tree.

     2. THE GUARD. A client layer can express adds, overrides and hides.
        It cannot express "the office master's block moved" or "the
        office master's block is gone" — those belong to the Office
        Master variant. So a transaction that would remove or reorder a
        baseline block inside a client variant is REFUSED, with a quiet
        toast pointing at the operation that does work ("hide it").
        A GROUP the office master declared is the same kind of thing:
        it is master structure, so a variant may not break it (reason
        'group'). A variant grouping its OWN blocks — or master blocks
        the master left ungrouped — is ordinary authorship and passes.
        Refusing is the only non-corrupting answer: the alternative is
        writing a layer that cannot round-trip.
   ═══════════════════════════════════════════════════════════════ */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { GHOST_KEY, type VariantView } from './variantOps';

export const variantPluginKey = new PluginKey('specStudioVariant');

/** The live variant a transaction is running against — read by the block
 *  clipboard's delete/hide switch (Tranche 4, T4) so "is this block office-
 *  master's" stands on the SAME `baselineIds` the provenance paint and the
 *  guard already use, no second lookup that could disagree. `null` outside a
 *  variant (the plugin is not installed there — see `SpecStudioCanvas`), or
 *  before the plugin has mounted. */
export function activeVariantView(state: EditorState): VariantView | null {
  const plugin = variantPluginKey.get(state) as
    | (Plugin & { getView?: () => VariantView | null })
    | undefined;
  return plugin?.getView?.() ?? null;
}

/** Ask for a repaint after something OUTSIDE the document changed — the
 *  derived layer, a revert, a stale acknowledgement. */
export const VARIANT_REBUILD = 'specStudio:variantRebuild';

export type VariantTagKind = 'override' | 'stale' | 'restore' | 'update';
export type VariantGateReason = 'remove' | 'reorder' | 'group';

export interface VariantPluginOptions {
  /** The live variant, read at decoration time — it changes under the editor. */
  getView: () => VariantView | null;
  /** A tag was clicked: revert, compare, restore, or open the update. */
  onTag?: (kind: VariantTagKind, blockId: string, anchor: DOMRect) => void;
  /** An edit a layer cannot express was refused. */
  onGate?: (reason: VariantGateReason) => void;
  /** Phase 3 — blocks carrying an undecided update from the office master.
   *  Read at decoration time like `getView`, so a decision repaints on the
   *  click that earned it (dispatch VARIANT_REBUILD). */
  getPending?: () => Set<string>;
}

const isGhost = (node: PMNode): boolean =>
  (node.attrs.meta as Record<string, unknown> | undefined)?.[GHOST_KEY] === true;

/** Baseline ids in document order, first occurrence only. A split leaves the
 *  same id on two nodes for one tick (`UniqueBlockIds` re-mints the second on
 *  the next), and that is a legal edit — so it must not read as a move. */
function baselineSequence(doc: PMNode, ids: Set<string>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  doc.forEach((node) => {
    const id = node.attrs.blockId as string | undefined;
    if (!id || !ids.has(id) || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

/**
 * Would this document change a group the OFFICE MASTER declared? Only blocks
 * the master actually grouped are checked, so a variant grouping its own
 * blocks (or master blocks the master left loose) passes straight through.
 */
export function breaksMasterGroup(doc: PMNode, view: VariantView): boolean {
  if (!view.masterGroupIds.size) return false;
  let broken = false;
  doc.forEach((node) => {
    if (broken) return;
    const id = (node.attrs.blockId as string) || '';
    const master = id ? view.masterGroupIds.get(id) : undefined;
    if (!master) return;
    if (((node.attrs.groupId as string) || '') !== master) broken = true;
  });
  return broken;
}

interface Tag {
  kind: VariantTagKind | 'layer';
  label: string;
  title?: string;
}

function tagRow(tags: Tag[], blockId: string, onTag?: VariantPluginOptions['onTag']): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'sps-tags';
  wrap.setAttribute('contenteditable', 'false');
  for (const tag of tags) {
    const interactive =
      tag.kind === 'override' ||
      tag.kind === 'stale' ||
      tag.kind === 'restore' ||
      tag.kind === 'update';
    const el = document.createElement(interactive ? 'button' : 'span');
    el.className = `sps-tag sps-tag-${tag.kind}`;
    el.textContent = tag.label;
    if (tag.title) el.setAttribute('title', tag.title);
    if (interactive) {
      (el as HTMLButtonElement).type = 'button';
      el.setAttribute('aria-label', tag.title || tag.label);
      el.addEventListener('mousedown', (e) => {
        // The caret must not move into the tag — it is chrome, not text.
        e.preventDefault();
        e.stopPropagation();
        onTag?.(tag.kind as VariantTagKind, blockId, (el as HTMLElement).getBoundingClientRect());
      });
    } else {
      el.setAttribute('aria-hidden', 'true');
    }
    wrap.appendChild(el);
  }
  return wrap;
}

function build(state: EditorState, opts: VariantPluginOptions): DecorationSet {
  const view = opts.getView();
  if (!view) return DecorationSet.empty;
  const decorations: Decoration[] = [];
  const pending = opts.getPending?.() ?? null;

  state.doc.forEach((node, pos) => {
    const id = (node.attrs.blockId as string) || '';
    if (!id) return;
    const ghost = isGhost(node);
    const inBase = view.baselineIds.has(id);
    const overridden = inBase && view.overriddenIds.has(id);
    const stale = overridden && view.staleIds.has(id);
    const hasUpdate = !!pending?.has(id);

    const classes: string[] = [];
    if (!inBase) classes.push('sps-prov-layer');
    if (overridden) classes.push('sps-prov-override');
    if (stale) classes.push('sps-prov-stale');
    if (hasUpdate) classes.push('sps-prov-update');
    if (ghost) classes.push('sps-ghost');

    const attrs: Record<string, string> = {};
    if (classes.length) attrs.class = classes.join(' ');
    // A ghost is a stub, not a paragraph you can type into. Restoring it is
    // the way back — nothing disappears silently (§2.4).
    if (ghost) attrs.contenteditable = 'false';
    if (Object.keys(attrs).length) {
      decorations.push(Decoration.node(pos, pos + node.nodeSize, attrs));
    }

    const tags: Tag[] = [];
    if (!inBase) tags.push({ kind: 'layer', label: view.clientName });
    // The office master moved on this block and nobody has answered yet.
    // First in the row: it is the one thing here that wants a decision.
    if (hasUpdate) {
      tags.push({
        kind: 'update',
        label: 'Update',
        title: 'The office master changed this block — click to accept or reject it',
      });
    }
    if (overridden && !ghost) {
      tags.push({
        kind: 'override',
        label: 'Overridden',
        title: 'Edited for this variant — click to revert to the office master text',
      });
    }
    if (stale) {
      tags.push({
        kind: 'stale',
        label: 'Core has changed',
        title: 'The office master moved on since this override — click to compare',
      });
    }
    if (ghost) {
      tags.push({
        kind: 'restore',
        label: 'Hidden — restore',
        title: 'Hidden in this variant. Click to bring it back.',
      });
    }
    if (!tags.length) return;

    // Paragraphs carry their tags at the end of the line (the number widget
    // owns the start); an atom — table, figure — carries them just before it.
    const atom = node.type.isAtom || node.type.name !== 'specParagraph';
    const at = atom ? pos : pos + node.nodeSize - 1;
    decorations.push(
      Decoration.widget(at, () => tagRow(tags, id, opts.onTag), {
        side: atom ? -1 : 1,
        key: `variant-${id}-${tags.map((t) => t.kind).join('.')}-${view.clientName}`,
      })
    );
  });

  return DecorationSet.create(state.doc, decorations);
}

export const VariantProvenance = Extension.create<VariantPluginOptions>({
  name: 'specStudioVariant',

  addOptions() {
    return { getView: () => null, onTag: undefined, onGate: undefined };
  },

  addProseMirrorPlugins() {
    const opts = () => this.options;
    const plugin: Plugin & { getView?: () => VariantView | null } = new Plugin({
      key: variantPluginKey,
      state: {
        init: (_config, state) => build(state, opts()),
        apply: (tr: Transaction, value: DecorationSet, _old, newState) => {
          if (tr.docChanged || tr.getMeta(VARIANT_REBUILD)) return build(newState, opts());
          return value.map(tr.mapping, tr.doc);
        },
      },
      filterTransaction: (tr, state) => {
        const o = opts();
        const view = o.getView();
        if (!view || !tr.docChanged) return true;
        const before = baselineSequence(state.doc, view.baselineIds);
        const after = baselineSequence(tr.doc, view.baselineIds);
        if (before.length !== after.length || before.some((id, i) => after[i] !== id)) {
          const reason: VariantGateReason = after.length < before.length ? 'remove' : 'reorder';
          // Refusing happens mid-dispatch; the toast waits for the tick to end.
          queueMicrotask(() => o.onGate?.(reason));
          return false;
        }
        if (breaksMasterGroup(tr.doc, view)) {
          queueMicrotask(() => o.onGate?.('group'));
          return false;
        }
        return true;
      },
      props: {
        decorations(state) {
          return variantPluginKey.getState(state) as DecorationSet;
        },
      },
    });
    // Live accessor for `activeVariantView` — a command outside this plugin
    // (the block clipboard's delete/hide switch) has no other way to reach
    // `getView` for the editor's CURRENT state.
    plugin.getView = () => opts().getView();
    return [plugin];
  },
});
